import Emitter from "@homer.core/emitter";
import scheduler from "@homer.core/scheduler";
import application from "@homer.core/application";
import api from "@homer.core/api";
import fragmentApi from "@homer.api/fragment";
import queue from "@homer.core/queue.raf";
import forEach from "@homer.utility/foreach";
import isUndefined from "@homer.utility/validator.isundefined";
import isInstanceof from "@homer.utility/validator.isinstanceof";
import types from "@homer.core/types";
import Uuid from "@homer.utility/uuid";
import reset from "@shared/reset";
import Canonical from "./canonical.js";
import Language from "./language.js";
import OpenGraph from "./opengraph.js";
import { name } from "../package.json";

let loadtime = null;
let loadstart;
let endpoint;
let request;
let previousScroll;

const html = document.documentElement;
const classList = html.classList;
const body = document.body;
const location = document.location;
const head = document.head;
const history = globalThis.history;
const nodes = {
    layout: head.querySelector(':scope > meta[itemprop="layout"]'),
    type: head.querySelector(':scope > meta[itemprop="type"]'),
    id: head.querySelector(':scope > meta[itemprop="id"]'),
    label: head.querySelector(':scope > meta[itemprop="label"]'),
    link: head.querySelector(':scope > meta[itemprop="link"]'),
    title: head.querySelector(":scope > title"),
    description: head.querySelector(':scope > meta[name="description"]'),
    robots: head.querySelector(':scope > meta[name="robots"]'),
};
const canonical = new Canonical(head.querySelectorAll(':scope > link[rel="canonical"]'));
const language = new Language(head.querySelectorAll(':scope > link[rel="alternate"][hreflang]'));
const opengraph = new OpenGraph(head.querySelectorAll(':scope > meta[property^="og:"], :scope > meta[property^="product:"]'));
const state = {
    current: Object.freeze(restoreState(history.state || {}, true)),
    previous: Object.freeze(restoreState(history.state && history.state.previous, false, false)),
};
const changeDetails = Object.freeze({
    fragment: Object.freeze({ source: "fragment" }),
    route: Object.freeze({ source: "route" }),
});
const routes = new Map();
// Demand resources self-invalidate by version; device detection remains valid across experiment switches.
const experimentResetOptions = Object.freeze({
    local: Object.freeze(["[demand:", "[@apfelkiste/device]"]),
    release: false,
});

if (history.scrollRestoration) {
    history.scrollRestoration = "manual";
}

function restoreState(state, isCurrent = false, DOMNeedsUpdate = true) {
    if (!state) {
        return null;
    }

    const sanitizedState = {
        uuid: Uuid.generate(),
        url: state.url || location.href,
        fragment: state.fragment || "content",
        loadtime: state.loadtime || null,
        previous: state.previous || null,
        next: state.next || null,
        position: state.position || 0,
        layout: state.layout || (isCurrent && getContent(nodes.layout)) || "shop",
        type: state.type || (isCurrent && getContent(nodes.type)) || null,
        id: state.id || (isCurrent && getContent(nodes.id, "integer")) || null,
        label: state.label || (isCurrent && getContent(nodes.label)) || null,
        link: state.link || (isCurrent && getContent(nodes.link)) || null,
        title: state.title || (isCurrent && document.title) || null,
        description: state.description || (isCurrent && getContent(nodes.description)) || null,
        robots: state.robots || (isCurrent && getContent(nodes.robots)) || null,
        canonical: state.canonical && state.canonical.length ? state.canonical : (isCurrent && canonical.current) || [],
        language: state.language && Object.keys(state.language).length ? state.language : (isCurrent && language.current) || {},
        opengraph:
            state.opengraph && Object.keys(state.opengraph).length ? state.opengraph : (isCurrent && opengraph.current) || {},
    };

    if (DOMNeedsUpdate) {
        updateDOM(sanitizedState);
    }

    return sanitizedState;
}

function reduceState(state) {
    if (!state) {
        return null;
    }

    return {
        uuid: Uuid.generate(),
        url: state.url,
        fragment: state.fragment,
        loadtime: state.loadtime,
        previous: { ...state.previous, previous: null },
        next: state.next,
        position: state.position,
        layout: state.layout,
        type: state.type,
        id: state.id,
        label: state.label,
        link: state.link,
        title: state.title,
        description: state.description,
        robots: state.robots,
        canonical: state.canonical,
        language: state.language,
        opengraph: state.opengraph,
    };
}

function getContent(node, type = "string") {
    let value = node.getAttribute("content");

    if (!value) {
        return null;
    }

    switch (type) {
        case "integer":
            value = parseInt(value, 10);
            break;
    }

    return value;
}

function updateDOM(state) {
    setAttribute(nodes.layout, "content", (state.layout || "").toString());
    setAttribute(nodes.type, "content", (state.type || "").toString());
    setAttribute(nodes.id, "content", (state.id || "").toString());
    setAttribute(nodes.label, "content", (state.label || "").toString());
    setAttribute(nodes.link, "content", (state.link || "").toString());
    setAttribute(nodes.description, "content", (state.description || "").toString());
    setAttribute(nodes.robots, "content", (state.robots || "").toString());

    canonical.update(state.canonical);
    language.update(state.language);
    opengraph.update(state.opengraph);

    nodes.title.textContent = document.title = state.title || "";
}

function setAttribute(node, attribute, value) {
    node.setAttribute(attribute, (value || "").toString());
}

function preload(context, url, fragment = "content") {
    url = URL.resolve(url);

    url.pathname = `/fragment${url.pathname}`;
    url.parameter = {
        ...url.parameter,
        render: {
            nonce: application.nonce,
            query: fragment,
            layout: state.current.layout,
            type: "fragment",
        },
    };

    const link = document.createElement("link");
    const remove = () => {
        link.detach();
    };

    link.addListener("load", remove, { once: true });
    link.addListener("abort", remove, { once: true });
    link.addListener("error", remove, { once: true });

    link.rel = "prefetch";
    link.as = "fetch";
    link.href = url.href;

    head.appendChild(link);
}

function redirect(context, url, forceReload = false) {
    url = URL.resolve(url);

    context.emit("intent:redirect", url.href, state.current, state.previous);

    if (!forceReload && url.isLocal) {
        return load(context, url.href);
    } else {
        location.href = url.href;
    }
}

async function load(context, url, fragment = "content", position = 0, popstate) {
    const intentEvent = await context.emit("intent:load", { url, fragment, position, popstate }, state.current, state.previous);

    if (intentEvent.isCanceled) return;

    if (request) {
        request.abort(name);
    }

    const resolved = URL.resolve(url);

    loadstart = performance.now();
    endpoint = `${resolved.pathname}${resolved.search}`;
    url = resolved.href;
    previousScroll = html.scrollTop;

    request = fragmentApi.client.getHTML(endpoint, { query: fragment, layout: state.current.layout });

    return request
        .then((response) => {
            if (!response) {
                throw new Error(`empty response for \`${endpoint}\``);
            }

            if (response.experiment !== application.experiment) {
                return reset(experimentResetOptions).finally(() => {
                    throw new Error("experiment mismatch");
                });
            }

            if (response.features !== application.features) {
                return reset(experimentResetOptions).finally(() => {
                    throw new Error("features mismatch");
                });
            }

            if (response.version !== application.version) {
                throw new Error("outdated version");
            }

            if (response.locale !== application.locale) {
                throw new Error("locale mismatch");
            }

            loadtime = Math.round(performance.now() - loadstart);
            loadstart = null;
            request = null;

            return Promise.resolve()
                .then(() => {
                    const { template } = response;
                    const updates = [];

                    forEach(template, (node, id) => {
                        updates.push(
                            new Promise((resolve) => {
                                queue.add(() => {
                                    const target = document.querySelector(`[data-id="${id}"]`);

                                    if (target) {
                                        // Replace the complete fragment root so changed attributes are applied as well.
                                        target.parentElement.replaceChild(node, target);
                                    }

                                    resolve();
                                    context.emit(`update:${id}`);
                                });
                            }),
                            /*scheduler.yield.medium().then(() => {
                                const target = document.querySelector(`[data-id="${id}"]`);

                                if (target) {
                                    target.parentElement.replaceChild(node, target);
                                }

                                context.emit(`update:${id}`);
                            }),*/
                        );
                    });

                    if (updates.length) {
                        context.emit(`intent:update`, state.current, state.previous);
                    }

                    return Promise.all(updates);
                })
                .then(() => {
                    url = URL.resolve(response.location);

                    if (resolved.hash) {
                        url.hash = resolved.hash;
                    }

                    if (url.searchParams.size > 0) {
                        url.searchParams.sort();
                    }

                    url = url.toString();

                    const page = response.meta;
                    const previous = !isUndefined(popstate)
                        ? popstate?.previous || null
                        : { ...(state.current || {}), next: null, position: previousScroll };
                    const current = !isUndefined(popstate)
                        ? restoreState(popstate, false, false)
                        : reduceState({
                              url,
                              fragment,
                              loadtime,
                              previous: reduceState(previous),
                              next: null,
                              position: position,
                              layout: page.layout,
                              type: page.type,
                              id: page.id,
                              label: page.label || "undefined",
                              link: page.link || "",
                              title: page.title,
                              description: page.description,
                              robots: page.robots,
                              canonical: page.linkCanonical ? [page.linkCanonical] : [],
                              language: page.linkLanguage || {},
                              opengraph: page.opengraph || {},
                          });

                    if (previous) {
                        previous.next = reduceState(current);
                    }

                    current.previous = previous ? reduceState(previous) : null;

                    state.previous = Object.freeze(previous);
                    state.current = Object.freeze(current);

                    updateDOM(current);
                    setAttribute(html, "itemtype", types[page.type || "default"] || types.default);
                    setAttribute(body, "data-type", page.type || "default");

                    if (!popstate) {
                        if (state.previous) {
                            history.replaceState(state.previous, "", state.previous.url);
                        }

                        if (!state.previous || state.previous.url !== state.current.url) {
                            history.pushState({ ...state.current, previous: state.previous || null }, "", state.current.url);
                        }
                    }

                    context.emit("change", state.current, state.previous, changeDetails.fragment);
                })
                .finally(() => {
                    scheduler.yield.medium().then(() => {
                        const hash = location.hash;

                        if (position === 0 && hash) {
                            const target = document.querySelector(`${hash},a[name='${hash.substring(1)}']`);

                            if (target) {
                                target.scrollIntoView();

                                return;
                            }
                        }

                        if (position !== null) {
                            html.scrollTop = position;
                        }

                        context.emit("intent:end", state.current, state.previous);
                    });
                });
        })
        .catch((error) => {
            if (isInstanceof(error, api.AbortError) && error?.data?.request?.aborted) {
                return;
            }

            page.redirect(url, true);
        });
}

function set(context, url, metadata = {}, position = 0, popstate) {
    metadata = metadata || {};

    context.emit("intent:set", { url, position, popstate }, state.current, state.previous);

    const previous =
        !isUndefined(popstate) && popstate.previous
            ? popstate.previous
            : { ...(state.current || {}), next: null, position: html.scrollTop };
    const current = !isUndefined(popstate)
        ? restoreState(popstate, false, false)
        : {
              ...(state.current || {}),
              ...metadata,
              url: URL.resolve(url).href,
              fragment: null,
              previous: reduceState(previous),
              next: null,
              position: position,
          };

    loadtime = 0;

    if (previous) {
        previous.next = reduceState(current);
    }

    state.previous = Object.freeze(previous);
    state.current = Object.freeze(current);

    // Route-only navigations may still update page metadata such as canonical or hreflang links.
    updateDOM(state.current);

    if (!popstate) {
        if (state.previous) {
            history.replaceState(state.previous, "", state.previous.url);
        }

        if (!state.previous || state.previous.url !== state.current.url) {
            history.pushState({ ...state.current, previous: state.previous || null }, "", state.current.url);
        }
    }

    context.emit("change", state.current, state.previous, changeDetails.route);
    context.emit("intent:end", state.current, state.previous);
}

function replace(context, url, metadata) {
    url = URL.resolve(url).href;
    metadata = metadata || {};

    state.current = Object.freeze(Object.assign({}, state.current, metadata, { url }));

    updateDOM(state.current);
    history.replaceState(state.current, "", url);
    context.emit("replace", state.current, state.previous);
}

const processState = (prefix, regex, state) => {
    const pathname = state ? URL.resolve(state.url).pathname : "";

    if (pathname.startsWith(prefix)) {
        return { ...state, route: pathname.replace(regex, "") };
    }

    return null;
};

const resolveRouteUrl = (prefix, slug = "") => {
    return `${prefix}${!slug || slug.startsWith("?") || slug.startsWith("#") ? "" : "/"}${slug}`;
};

const normalizeRouteSlug = (options, slug = "") => {
    return options.normalize?.(slug || "") ?? slug;
};

class Route extends Emitter {
    constructor(prefix, defaultSlug = null, options = {}) {
        super();
        const regex = new RegExp(`^${prefix}\/?`);
        const emitRouteState = (name, current, previous) => {
            const currentRoute = processState(prefix, regex, current);
            const previousRoute = processState(prefix, regex, previous);

            if (currentRoute) {
                this.emit(name, Object.freeze(currentRoute), previousRoute ? Object.freeze(previousRoute) : null);
            }
        };

        const { state } = routes
            .set(this, {
                prefix,
                regex,
                defaultSlug,
                options: options || {},
                listener: [
                    page.addListener("change", (event, current, previous) => {
                        emitRouteState("change", current, previous);
                    }),
                    page.addListener("replace", (event, current, previous) => {
                        emitRouteState("replace", current, previous);
                    }),
                    // Keep route state aligned with both history pushes and in-place replacements.
                    this.addListener(/^(?:change|replace)$/, (event, current, previous) => {
                        state.current = current;
                        state.previous = previous;
                    }),
                ],
                state: {},
            })
            .get(this);

        state.current = Object.freeze(processState(prefix, regex, page.current));
        state.previous = Object.freeze(processState(prefix, regex, page.previous));

        if (!state.current) {
            return;
        }

        const currentSlug = this.resolve(state.current.url);
        const normalizedSlug = normalizeRouteSlug(options, currentSlug);

        if (state.current?.route === "" && defaultSlug) {
            this.replace(normalizedSlug);
        } else if (normalizedSlug !== currentSlug) {
            this.replace(normalizedSlug);
        } else if (options.metadata) {
            // Initial route attaches must publish route metadata before the next route-only navigation.
            page.replace(state.current.url, options.metadata(normalizedSlug));
        } else {
            return;
        }

        state.current = Object.freeze(processState(prefix, regex, page.current));
    }

    get current() {
        const { state } = routes.get(this);

        return state.current;
    }

    get previous() {
        const { state } = routes.get(this);

        return state.previous;
    }

    release() {
        const { listener } = routes.get(this);

        routes.delete(this);
        page.removeListener(...listener);
        this.removeListener(...listener);
    }

    static resolve(pathname) {
        for (const [route, { prefix }] of routes) {
            if (pathname.startsWith(prefix)) {
                return route;
            }
        }

        return null;
    }

    resolve(value) {
        const { prefix, regex } = routes.get(this);

        try {
            const url = value instanceof URL ? value : new URL(value, location.origin);

            if (url.pathname.startsWith(prefix)) {
                return `${url.pathname.replace(regex, "")}${url.search || ""}${url.hash || ""}`;
            }
        } catch {
            // Invalid values are already route-relative slugs from component callsites.
        }

        return value?.toString() || "";
    }

    set(slug, metadata) {
        const { defaultSlug, options, prefix } = routes.get(this);

        if (defaultSlug && (!slug || slug.startsWith("?") || slug.startsWith("#"))) {
            this.replace(slug, metadata);

            return;
        }

        slug = normalizeRouteSlug(options, slug);

        page.set(resolveRouteUrl(prefix, slug), {
            ...(options.metadata?.(slug) || {}),
            ...(metadata || {}),
        });
    }

    replace(slug, metadata) {
        const { defaultSlug, options, prefix } = routes.get(this);

        slug = normalizeRouteSlug(options, slug);

        if (defaultSlug && (!slug || slug.startsWith("?") || slug.startsWith("#"))) {
            slug = `${defaultSlug.startsWith("/") ? defaultSlug.slice(1) : defaultSlug}${slug || ""}`;
        }

        page.replace(resolveRouteUrl(prefix, slug), {
            ...(options.metadata?.(slug) || {}),
            ...(metadata || {}),
        });
    }
}

class Page extends Emitter {
    constructor() {
        super();

        globalThis.addEventListener("popstate", (event) => {
            if (event.state === null) {
                event.preventDefault();

                return;
            }

            const current = event.state;
            const pathname = URL.resolve(current.url).pathname;
            const { next } = current;
            const fragment = next && pathname === URL.resolve(next.url).pathname ? current.fragment : null;

            if (Route.resolve(pathname)) {
                set(this, current.url || location.pathname, {}, current.position || undefined, current);
            } else {
                load(this, current.url || location.pathname, fragment || undefined, current.position || undefined, current);
            }
        });

        globalThis.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                // BFCache/WebView restores keep the page state alive, so expose an explicit page-level restore event.
                this.emit("restore", state.current, state.previous, { persisted: true });
            }
        });

        Object.defineProperties(this, {
            uuid: {
                get: () => state.current.uuid,
                enumerable: true,
            },
            url: {
                get: () => state.current.url,
                enumerable: true,
            },
            loadtime: {
                get: () => state.current.loadtime,
                enumerable: true,
            },
            type: {
                get: () => state.current.type || null,
                enumerable: true,
            },
            id: {
                get: () => state.current.id || null,
                enumerable: true,
            },
            label: {
                get: () => state.current.label || null,
                enumerable: true,
            },
        });

        this.on(/^intent:(?:load|set)$/, () => {
            classList.add("isTransitioning");
        }).on("intent:end", () => {
            classList.remove("isTransitioning");
        });
    }

    get isTransitioning() {
        return classList.has("isTransitioning");
    }

    get fragment() {
        return state.current.fragment;
    }

    get layout() {
        return state.current.layout;
    }

    get position() {
        return state.current.position;
    }

    get title() {
        return state.current.title;
    }

    get description() {
        return state.current.description;
    }

    get robots() {
        return state.current.robots;
    }

    get canonical() {
        return state.current.canonical;
    }

    get language() {
        return state.current.language;
    }

    get opengraph() {
        return state.current.opengraph;
    }

    get referrer() {
        return state.current?.previous?.url || document.referrer || null;
    }

    get breadcrumb() {
        try {
            const node = document.querySelector('script[type="page/breadcrumb"]');

            return JSON.parse(node.textContent);
        } catch (error) {
            // purposely ignore
        }

        return [];
    }

    get current() {
        return state.current;
    }

    get previous() {
        return state.previous;
    }

    get Route() {
        return Route;
    }

    abort(reason) {
        if (request) {
            request.abort(reason);

            request = null;
        }
    }

    preload(url, fragment) {
        return preload(this, url || location.pathname, fragment || undefined);
    }

    redirect(url, forceReload = false) {
        return redirect(this, url, forceReload);
    }

    load(url, fragment, position) {
        return load(this, url || location.pathname, fragment || undefined, position);
    }

    set(url, metadata = {}) {
        return set(this, url || location.pathname, metadata);
    }

    replace(url, metadata = {}) {
        return replace(this, url || location.pathname, metadata);
    }
}

const page = new Page();

if (location.hash) {
    location.hash = "";

    page.replace(location.href.slice(0, -1));
}

export default page;
